import {
  CptySettlementTypeEnum,
  type CareNewOrderSingle,
  type CareOrder,
  type Leaves,
  type OrdStatusEnum,
} from '@talos/kyoko';
import { isEqual as isDateEqual, isToday } from 'date-fns';
import { get, isEqual } from 'lodash-es';
import { buildCareOrderRequest } from '../../../../components/OMS/CareOrderFormView/CareOrderService';
import type { CareOrderParser, ImportCareOrderActionType, ImportedCareOrder, SimilarCareOrder } from './types';

type OverlappingFields = Leaves<CareOrder> | Leaves<CareNewOrderSingle>;
interface FieldDetails<V = any> {
  /** The field (eg "Symbol" or "Group") to grab the property from */
  field: OverlappingFields;
  /** Custom isEqual function to perform comparison between the imported and similar care order. Defaults to lodash.isEqual */
  isEqualFn?: (a: V | undefined, b: V | undefined) => boolean;
  /**
   * Denotes the fields that _cannot_ be shared across several care orders. These are ID fields.
   * For example, our users use the CareOrder.Group field to store their system's OrderID. If when importing we notice a (non-rejected/canceled)
   * CO with the same Group property, we _do not let them create a new care order_. They can either modify, or ignore.
   */
  unique?: boolean;
}

interface ParserFieldInfo {
  /** Denotes the fields that must match in order for the compared care order to be "similar" to the import candidate */
  requiredFields: FieldDetails[];
  /**
   * Denotes all fields relevant for our similarity / duplication checking. Fields not present in this list are ignored when comparing
   * the imported care order to existing care orders.
   */
  allFields: FieldDetails[];
}

const PARSER_FIELDS: { [key in CareOrderParser]: ParserFieldInfo } = {
  aladdin: {
    requiredFields: [{ field: 'Symbol' }, { field: 'Side' }, { field: 'OrderQty' }, { field: 'Currency' }],
    allFields: [
      { field: 'Symbol' },
      { field: 'Side' },
      { field: 'OrderQty' },
      { field: 'Currency' },
      { field: 'Annotations.ETP.InitiatingFrom' },
      { field: 'Group' },
      { field: 'Counterparty' },
    ],
  },
  unqork: {
    requiredFields: [
      { field: 'units' },
      { field: 'Annotations.BasketOrder.BasketSymbol' },
      { field: 'Side' },
      { field: 'CptySettlementType', isEqualFn: importedUnqorkSettlementTypeMatchesCareOrder },
    ],
    allFields: [
      { field: 'units' },
      { field: 'Annotations.BasketOrder.BasketSymbol' },
      { field: 'Side' },
      { field: 'CptySettlementType', isEqualFn: importedUnqorkSettlementTypeMatchesCareOrder },
      { field: 'Annotations.ETP.InitiatingFrom' },
      { field: 'Group', unique: true },
      { field: 'Counterparty' },
      {
        field: 'SettleDate',
        isEqualFn: (a: string | undefined, b: string | undefined) => {
          const dateA = a ? new Date(a) : undefined;
          const dateB = b ? new Date(b) : undefined;
          if (!dateA || !dateB) {
            // If both undefined, true, else false
            return dateA === dateB;
          }

          return isDateEqual(dateA, dateB);
        },
      },
    ],
  },
};

function getRelevantCareOrdersToCheckImportAgainst(allCareOrders: CareOrder[]): CareOrder[] {
  // For now, we only check the import against care orders submitted today. In the future, I anticipate this function becoming more complex.
  return allCareOrders.filter(co => isToday(new Date(co.SubmitTime)));
}

/** Given the imported care order and all care orders in the system, returns a list of SimilarCareOrders which can be used for modification */
function getSimilarCareOrders(
  importedCareOrder: ImportedCareOrder,
  careOrders: CareOrder[],
  parser: CareOrderParser
): SimilarCareOrder[] {
  const matchingCareOrders = careOrders.filter(careOrder => {
    // If any must-match field is not equal, return false. Signeage is flipped to juice the performance
    return !PARSER_FIELDS[parser].requiredFields.some(
      fieldDef => !areFieldsEqual(importedCareOrder, careOrder, fieldDef)
    );
  });

  return matchingCareOrders.map(co => createSimilarCareOrder(importedCareOrder, co, parser));
}

/**
 * Given the imported care order and the existing care order we're comparing it to, creates the SimilarCareOrder entity which
 * can then be selected for modification
 */
function createSimilarCareOrder(
  importedCO: ImportedCareOrder,
  existingCO: CareOrder,
  parser: CareOrderParser
): SimilarCareOrder {
  // Compare the two COs across a set of fields, and grab all the fields which would represent an invalid modification
  const invalidFields = PARSER_FIELDS[parser].allFields
    .filter(fieldDef => !isFieldModificationValid(importedCO, existingCO, fieldDef))
    .map(invalidField => invalidField.field);

  const matchesUniquely = PARSER_FIELDS[parser].allFields.some(fieldDef => {
    if (!fieldDef.unique) {
      return false;
    }

    if (DUPLICATE_EXEMPT_STATES.has(existingCO.OrdStatus)) {
      // The existing CO, even if it might match uniquely, is "dead", so it can be ignored for the purpose of matching
      // Use case here is that the user should be able to re-create this Care Order with the same Group (external OrderID)
      return false;
    }

    const isEitherPropertyEmpty =
      isPropertyEmpty(get(importedCO, fieldDef.field)) || isPropertyEmpty(get(existingCO, fieldDef.field));
    if (isEitherPropertyEmpty) {
      // Unique matches can only occur on non-empty properties
      return false;
    }

    return areFieldsEqual(importedCO, existingCO, fieldDef);
  });

  return {
    careOrder: existingCO,
    rowID: existingCO.OrderID,
    matchesUniquely,
    warning:
      invalidFields.length > 0
        ? {
            invalidFields,
          }
        : undefined,
  };
}

/**
 * We are not allowed to overwrite an existing property. We can only accept a modification if it writes into an empty field,
 * or the new "write" is the exact same as what's already written (no change). If this isn't the case, we return false.
 */
function isFieldModificationValid(
  importedCO: ImportedCareOrder,
  existingCO: CareOrder,
  { field, isEqualFn = isEqual }: FieldDetails
) {
  const importedValue = get(importedCO, field);
  const existingValue = get(existingCO, field);

  if (importedValue == null || importedValue === '') {
    // Imported value is empty. We can always modify in this case. If the existingCO's value is defined, it'll remain.
    return true;
  } else {
    // Imported value is populated. We only allow import if either there is no existing value, or the existing value matches.
    return existingValue == null || isEqualFn(importedValue, existingValue);
  }
}

// If a CareOrder has been rejected or canceled, we want to allow the user to re-import a duplicate of that care order.
// To achieve this, we exempt these two states from being marked as duplicate, which would block the import modification.
// There are unresolved issues here -- like -- what happens if we cancel a partially filled order? For now, we ignore this.
const DUPLICATE_EXEMPT_STATES: Set<OrdStatusEnum> = new Set(['Rejected', 'Canceled']);

// We only really work with strings in here so this is okay, but the proper value type is looser so need to use unknown here
function isPropertyEmpty(value: unknown): boolean {
  return value == null || value === '';
}

function areFieldsEqual(ico: ImportedCareOrder, co: CareOrder, fieldDef: FieldDetails): boolean {
  const icoValue = get(ico, fieldDef.field);
  const coValue = get(co, fieldDef.field);

  const bothEmpty = isPropertyEmpty(icoValue) && isPropertyEmpty(coValue);
  if (bothEmpty) {
    // We're effectively saying that "" and null are "equal" here (both are empty)
    return true;
  }

  const isEqualFn = fieldDef.isEqualFn ?? isEqual;
  return isEqualFn(icoValue, coValue);
}

function importedUnqorkSettlementTypeMatchesCareOrder(
  importedSettlementType: CptySettlementTypeEnum | undefined,
  careOrderSettlementType: CptySettlementTypeEnum | undefined
) {
  // Unqork orders can for some reason have settlement type be nullish initially. We still want to allow matching / creation in this case,
  // and we trust the user to know what settlement type the unqork order actually is. We let an undefined settlement type match with either talos CO's cash or inkind.
  if (isPropertyEmpty(importedSettlementType)) {
    return true;
  }

  // Else, if the unqork co has some settlementType defined, then we require it to match with the talos co
  return importedSettlementType === careOrderSettlementType;
}

/**
 * Returns whether or not we can find any care order in the system which is a "superset" of the imported care order.
 * If the imported CO doesn't specify a non-required field, the existing CO trivially matches regardless of its value for that field.
 * This means that an existing CO with _more_ information than the imported CO is still considered a duplicate.
 * However, if a required field is empty on the imported CO, we never consider it a duplicate match.
 * Field-specific equality functions can intentionally override this, for example Unqork settlement type treats an empty
 * imported value as matching any existing care order settlement type.
 */
function getDuplicateAliveCareOrders(ico: ImportedCareOrder, careOrders: CareOrder[], parser: CareOrderParser) {
  const requiredFieldNames = new Set(PARSER_FIELDS[parser].requiredFields.map(f => f.field));

  return careOrders.filter(co => {
    if (DUPLICATE_EXEMPT_STATES.has(co.OrdStatus)) {
      return false; // exempt, cannot be a duplicate
    }

    return PARSER_FIELDS[parser].allFields.every(fieldDef => {
      const importedValue = get(ico, fieldDef.field);
      if (isPropertyEmpty(importedValue) && !requiredFieldNames.has(fieldDef.field)) {
        // Empty incoming properties are OK as long as it is not a required field
        return true;
      }

      return areFieldsEqual(ico, co, fieldDef);
    });
  });
}

/** Returns new ImportedCareOrders, now with their Actions populated. Does not mutate the passed array, creates a new one. */
export function getImportedCareOrdersWithActions(
  importedCareOrders: ImportedCareOrder[],
  /** All care orders in the system */
  allCareOrders: CareOrder[],
  parser: CareOrderParser
): ImportedCareOrder[] {
  const relevantCareOrders = getRelevantCareOrdersToCheckImportAgainst(allCareOrders);

  return importedCareOrders.map(ico => {
    const duplicates = getDuplicateAliveCareOrders(ico, relevantCareOrders, parser);
    const similarCareOrders = getSimilarCareOrders(ico, relevantCareOrders, parser);

    // We only check the uniqueness condition within the set of similar care orders. We are inviting a corner case here, however...
    // If the client has >1 CO with the same unique property across different "similarity sets" (defined as Qty, Side, Symbol), then
    // the first one must have been a mistake and been cancelled. Making this... okay.
    const uniqueMatch = similarCareOrders.find(co => co.matchesUniquely);

    if (uniqueMatch) {
      const uniqueMatchIsDuplicate = duplicates.some(duplicate => duplicate.OrderID === uniqueMatch.careOrder.OrderID);
      return {
        ...ico,
        action: {
          // If the unique match is a duplicate, there is nothing for the user to do, so we default the action to Ignore
          type: uniqueMatchIsDuplicate ? 'ignore' : 'unknown',
          // This is the key thing that changes when we find a "unique" match: the unique match is forced to be the only similar care order
          similarCareOrders: [uniqueMatch],
          // When we find a unique match, we can only modify the existing CO or ignore the imported CO. We cannot create a new CO.
          canCreateNewCareOrder: false,
        },
      } satisfies ImportedCareOrder;
    }

    const actionType: ImportCareOrderActionType =
      duplicates.length > 0 ? 'ignore' : similarCareOrders.length === 0 ? 'create' : 'unknown';

    return {
      ...ico,
      action: { type: actionType, similarCareOrders, canCreateNewCareOrder: true },
    } satisfies ImportedCareOrder;
  });
}

/**
 * Depending on the selected action in the provided ImportedCareOrder, will return a websocket request to make
 * to perform this action. Returned will be either a CARE_NEW_ORDER_SINGLE for the "create" action, or a
 * CARE_CANCEL_REPLACE_REQUEST for the "modify" action.
 */
export function importedCareOrderToRequest(importedCareOrder: ImportedCareOrder, useFundDefinitions: boolean) {
  const existingCO =
    importedCareOrder.action.type === 'modify' ? importedCareOrder.action.careOrderToModify : undefined;
  return buildCareOrderRequest({
    clOrdID: importedCareOrder.ClOrdID,

    // Required fields. These are fields we match for similarities on. They will always be set correctly by the importedCareOrder itself.
    symbol: importedCareOrder.Symbol,
    side: importedCareOrder.Side,
    quantity: importedCareOrder.OrderQty, // this will be either units or actual orderqty (distinguished by the currency below)
    currency: importedCareOrder.Currency,

    // Not required fields. For these, the imported CO might have less information than the existing CO. So we make sure here to not lose any info.
    group: importedCareOrder.Group || existingCO?.Group,
    comments: importedCareOrder.Comments || existingCO?.Comments,
    counterparty: importedCareOrder.Counterparty || existingCO?.Counterparty,
    initiatingFrom: importedCareOrder.Annotations?.ETP?.InitiatingFrom || existingCO?.Annotations?.ETP?.InitiatingFrom,
    settleDate: importedCareOrder.SettleDate || existingCO?.SettleDate,
    settleType: importedCareOrder.CptySettlementType || existingCO?.CptySettlementType || CptySettlementTypeEnum.Cash,
    fund:
      importedCareOrder.Annotations?.BasketOrder?.BasketSymbol || existingCO?.Annotations?.BasketOrder?.BasketSymbol,
    subAccount: importedCareOrder.SubAccount || existingCO?.SubAccount,

    // Fields not yet implemented for importing at all (just use whatever already exists, or nothing at all of course)
    externalGroup: existingCO?.ExternalGroup,
    externalComments: existingCO?.ExternalComments,

    careOrderBeingModified: existingCO,
    passFundUnitsAsOrderQty: useFundDefinitions,
  });
}
